(function () {
  'use strict';

  const property = '_bb_gwp';

  function isSample(item, config) {
    return ['sample', 'forced'].includes(item.properties?.[property]) ||
      (item.original_price === 0 && config.products.some(product => product.productId === item.product_id));
  }

  function isWrapping(item, config) {
    return item.properties?._bb_gift_wrapping === 'true' ||
      (!!config.wrapping?.productId && item.product_id === config.wrapping.productId && item.original_price === 0);
  }

  function evaluateWrapping(cart, config) {
    const wrapping = config.wrapping || {};
    const lines = cart.items.filter(item => isWrapping(item, config));
    const merchandise = cart.items.filter(item => !isWrapping(item, config) && !isSample(item, config) && item.final_line_price > 0);
    const discount = (cart.cart_level_discount_applications || []).reduce((sum, entry) => sum + entry.total_allocated_amount, 0);
    const amount = Math.max(0, merchandise.reduce((sum, item) => sum + item.final_line_price, 0) - discount);
    const threshold = Number(wrapping.threshold);
    const validThreshold = wrapping.threshold !== '' && wrapping.threshold != null && Number.isFinite(threshold) && threshold >= 0;
    const available = !!(wrapping.enabled && wrapping.variantId && wrapping.available && wrapping.price === 0);
    const currencyMatches = cart.currency === config.shopCurrency;
    const eligible = available && validThreshold && currencyMatches && merchandise.length > 0 && amount >= Math.round(threshold * 100);
    const updates = {};
    let selected = false;
    lines.forEach(item => {
      const keep = eligible && !selected && item.variant_id === wrapping.variantId && item.original_price === 0;
      if (keep) selected = true;
      const quantity = keep ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    return { available, eligible, selected, lines, updates, currencyMatches, validThreshold,
      hasMerchandise: merchandise.length > 0, remaining: Math.max(0, Math.round(threshold * 100) - amount) };
  }

  function evaluate(cart, config) {
    const samples = cart.items.filter(item => isSample(item, config));
    const merchandise = cart.items.filter(item => !isSample(item, config) && !isWrapping(item, config) && item.final_line_price > 0);
    const maximum = Math.min(config.forced ? 1 : 20, Math.max(0, Math.floor(Number(config.maximum) || 0)));
    const threshold = Number(config.threshold);
    const validThreshold = config.threshold !== '' && config.threshold != null && Number.isFinite(threshold) && threshold >= 0;
    const quantity = merchandise.reduce((sum, item) => sum + item.quantity, 0);
    // Cart-level discounts are subtracted conservatively; gifts never earn eligibility.
    const cartDiscount = (cart.cart_level_discount_applications || []).reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
    const amount = Math.max(0, merchandise.reduce((sum, item) => sum + item.final_line_price, 0) - cartDiscount);
    let eligible = false;
    if (config.enabled && maximum > 0 && quantity > 0) {
      if (config.method === '1') eligible = validThreshold && cart.currency === config.shopCurrency && amount >= Math.round(threshold * 100);
      if (config.method === '2') eligible = validThreshold && Number.isInteger(threshold) && quantity >= threshold;
      if (config.method === '3') eligible = !!config.qualifyingProduct && merchandise.some(item => item.product_id === config.qualifyingProduct);
    }

    const selected = [];
    const updates = {};
    samples.forEach(item => {
      const product = config.products.find(product => product.productId === item.product_id && product.variantId === item.variant_id);
      const keep = eligible && product?.available && product.price === 0 && item.original_price === 0 &&
        selected.length < maximum && !selected.includes(item.product_id);
      if (keep) selected.push(item.product_id);
      const desired = keep ? 1 : 0;
      if (item.quantity !== desired) updates[item.key] = desired;
    });
    const forcedProduct = config.forced && config.products[0];
    const add = eligible && forcedProduct?.available && forcedProduct.price === 0 && !selected.includes(forcedProduct.productId)
      ? { id: forcedProduct.variantId, quantity: 1, properties: { [property]: 'forced' } } : null;
    return { eligible, maximum, samples, selected, updates, add };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { evaluate, isSample, evaluateWrapping };
  if (typeof document === 'undefined') return;
  const configElement = document.getElementById('BBLegacyGWPConfig');
  if (!configElement || window.BBLegacyGWP) return;
  const config = JSON.parse(configElement.textContent);

  class GWPController {
    constructor() {
      this.busy = false;
      this.emitting = false;
      this.cart = null;
      this.pending = false;
      this.timer = null;
      this.messageTimer = null;
      this.needsRefresh = false;
      document.addEventListener('bb-legacy:cart-added', () => {
        if (!this.emitting) this.schedule();
      });
      document.addEventListener('bb-legacy:cart-updated', () => {
        if (!this.emitting) this.schedule();
      });
      document.addEventListener('shopify:section:load', () => this.schedule());
      window.addEventListener('pageshow', () => this.schedule());
      document.addEventListener('click', event => this.onClick(event), true);
      document.addEventListener('submit', event => {
        if ((this.busy || this.needsRefresh) && (event.target.closest('cart-items, cart-drawer') || event.submitter?.name === 'checkout')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.message(this.needsRefresh ? 'Please refresh the page before checkout.' : 'Please wait for your cart to finish updating.');
        }
      }, true);
      document.addEventListener('change', event => {
        if (this.busy && event.target.name === 'updates[]') {
          event.stopImmediatePropagation();
          event.target.value = event.target.defaultValue;
        }
      }, true);
      this.schedule();
    }

    schedule() {
      if (this.busy) {
        this.pending = true;
        return;
      }
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.run(), 80);
    }

    async request(url, body) {
      const response = await fetch(url, body ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
      const data = await response.json();
      if (!response.ok || data.errors || data.status >= 400) {
        throw new Error(typeof data.description === 'string' ? data.description : 'Your cart could not be updated. Please try again.');
      }
      return data;
    }

    cartURL() {
      return `${window.Shopify?.routes?.root || '/'}cart.js`;
    }

    async normalize(cart, allowAdd = true) {
      const state = evaluate(cart, config);
      Object.assign(state.updates, evaluateWrapping(cart, config).updates);
      let changed = false;
      if (Object.keys(state.updates).length) {
        cart = await this.request(window.routes.cart_update_url, { updates: state.updates });
        changed = true;
      }
      const add = allowAdd && evaluate(cart, config).add;
      if (add) {
        const added = await this.request(window.routes.cart_add_url, { items: [add] });
        this.emitting = true;
        document.dispatchEvent(new CustomEvent('bb-legacy:cart-added', { detail: { item: added.items[0], quantity: 1 } }));
        this.emitting = false;
        cart = await this.request(this.cartURL());
        changed = true;
      }
      return { cart, changed };
    }

    async run(productId, toggleWrapping = false, selectOnly = false) {
      if (this.busy) return;
      clearTimeout(this.timer);
      this.busy = true;
      document.documentElement.setAttribute('data-bb-gwp-busy', '');
      this.paint();
      let changed = false;
      let errorMessage = '';
      try {
        let normalized = await this.normalize(await this.request(this.cartURL()));
        let cart = normalized.cart;
        changed = normalized.changed;
        if (productId && !config.forced) {
          const state = evaluate(cart, config);
          const product = config.products.find(product => product.productId === productId);
          if (!state.eligible || !product?.available || product.price !== 0) {
            throw new Error('This sample is no longer available for your cart.');
          }
          if (state.selected.includes(productId) && !selectOnly) {
            const updates = {};
            state.samples.filter(item => item.product_id === productId).forEach(item => { updates[item.key] = 0; });
            cart = await this.request(window.routes.cart_update_url, { updates });
            changed = true;
          } else if (!state.selected.includes(productId)) {
            if (state.selected.length >= state.maximum && state.maximum !== 1) {
              throw new Error('Remove a selected sample before choosing another.');
            }
            // Add first so a sold-out replacement never deletes the customer's current choice.
            const added = await this.request(window.routes.cart_add_url, {
              items: [{ id: product.variantId, quantity: 1, properties: { [property]: 'sample' } }]
            });
            changed = true;
            if (state.maximum === 1 && state.samples.length) {
              const fresh = await this.request(this.cartURL());
              const updates = {};
              fresh.items.filter(item => isSample(item, config) && item.product_id !== productId)
                .forEach(item => { updates[item.key] = 0; });
              if (Object.keys(updates).length) await this.request(window.routes.cart_update_url, { updates });
            }
            this.emitting = true;
            document.dispatchEvent(new CustomEvent('bb-legacy:cart-added', { detail: { item: added.items[0], quantity: 1 } }));
            this.emitting = false;
            cart = await this.request(this.cartURL());
          }
          normalized = await this.normalize(cart);
          cart = normalized.cart;
          changed = changed || normalized.changed;
        }
        if (toggleWrapping) {
          const state = evaluateWrapping(cart, config);
          if (state.selected) {
            const updates = {};
            state.lines.forEach(item => { updates[item.key] = 0; });
            cart = await this.request(window.routes.cart_update_url, { updates });
          } else {
            if (!state.eligible) throw new Error('Gift wrapping is no longer available for your cart.');
            const added = await this.request(window.routes.cart_add_url, {
              items: [{ id: config.wrapping.variantId, quantity: 1, properties: { _bb_gift_wrapping: 'true' } }]
            });
            changed = true;
            this.emitting = true;
            document.dispatchEvent(new CustomEvent('bb-legacy:cart-added', { detail: { item: added.items[0], quantity: 1 } }));
            this.emitting = false;
            cart = await this.request(this.cartURL());
          }
          changed = true;
          cart = (await this.normalize(cart)).cart;
        }
        this.cart = cart;
      } catch (error) {
        errorMessage = error.message || 'Your cart extras could not be updated. Please try again.';
        // Recover from partial mutations and expired inventory without trusting stale UI.
        try {
          const recovered = await this.normalize(await this.request(this.cartURL()), false);
          this.cart = recovered.cart;
          changed = true;
        } catch (_) {
          this.cart = null;
          this.needsRefresh = true;
        }
      }
      try {
        if (this.cart && (changed || this.needsRefresh)) {
          await this.render();
          this.needsRefresh = false;
        }
      } catch (_) {
        this.needsRefresh = true;
        errorMessage = 'Your cart changed, but could not be displayed. Please refresh the page before checkout.';
      } finally {
        this.busy = false;
        document.documentElement.removeAttribute('data-bb-gwp-busy');
        this.paint();
        if (this.cart) {
          this.emitting = true;
          document.dispatchEvent(new CustomEvent('bb-legacy:cart-updated', { detail: this.cart }));
          this.emitting = false;
        }
        if (errorMessage) this.message(errorMessage);
        if (this.pending) {
          this.pending = false;
          this.schedule();
        }
      }
    }

    async render() {
      const mainId = document.getElementById('main-cart-items')?.dataset.id;
      const sections = [];
      if (document.getElementById('CartDrawer-Body')) sections.push({ id: 'cart-drawer', selector: '#CartDrawer-Body' });
      if (mainId) sections.push({ id: mainId, selector: `#shopify-section-${mainId} cart-items` });
      if (sections.length) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sections.map(section => section.id).join(','));
        const html = await this.request(url.toString());
        const replacements = sections.map(section => {
          if (!html[section.id]) throw new Error('Missing cart section');
          const source = new DOMParser().parseFromString(html[section.id], 'text/html').querySelector(section.selector);
          const target = document.querySelector(section.selector);
          if (!source || !target) throw new Error('Missing cart content');
          return { source, target };
        });
        replacements.forEach(({ source, target }) => { target.innerHTML = source.innerHTML; });
      }
      document.querySelectorAll('.cart-count-badge, .cart-drawer__title-counter').forEach(counter => {
        counter.textContent = this.cart.item_count;
        if (counter.classList.contains('cart-count-badge')) counter.classList.toggle('hidden', this.cart.item_count === 0);
      });
      if (typeof handleCartInputNames === 'function') handleCartInputNames();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
    }

    paint() {
      const wrapping = this.cart ? evaluateWrapping(this.cart, config) : null;
      document.querySelectorAll('bb-legacy-gift-wrapping').forEach(element => {
        element.hidden = !wrapping?.hasMerchandise || !config.wrapping?.enabled;
        element.setAttribute('aria-busy', String(this.busy));
        const button = element.querySelector('[data-gift-wrapping]');
        button.disabled = this.busy || !wrapping?.eligible;
        button.setAttribute('aria-pressed', String(!!wrapping?.selected));
        element.querySelector('[data-wrap-label]').textContent = wrapping?.selected ? 'Remove gift wrapping' : 'Add gift wrapping';
        let status = 'Gift wrapping is currently unavailable.';
        if (wrapping?.available && wrapping.validThreshold && wrapping.currencyMatches) {
          const remaining = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.cart.currency }).format(wrapping.remaining / 100);
          status = wrapping.eligible ? (wrapping.selected ? 'Complimentary gift wrapping added.' : 'Complimentary gift wrapping is available.') : `Spend ${remaining} more for complimentary gift wrapping.`;
        }
        element.querySelector('[data-wrap-status]').textContent = this.busy ? 'Updating cart…' : status;
      });
      const state = this.cart ? evaluate(this.cart, config) : null;
      document.querySelectorAll('bb-legacy-gwp').forEach(element => {
        element.hidden = !state?.eligible || !element.querySelector('[data-gwp-product]');
        element.setAttribute('aria-busy', String(this.busy));
        element.querySelector('[data-gwp-status]').textContent = this.busy ? 'Updating samples…' :
          (config.forced ? (state?.selected.length ? 'Your complimentary sample is included automatically.' : 'Your complimentary sample could not be added. Please try again by refreshing the cart.') : `${state?.selected.length || 0} of ${state?.maximum || config.maximum} samples selected`);
      });
      document.querySelectorAll('[data-gwp-product]').forEach(button => {
        const selected = state?.selected.includes(Number(button.dataset.gwpProduct)) || false;
        button.disabled = this.busy || !!config.forced || !state?.eligible;
        button.setAttribute('aria-pressed', String(selected));
        const label = button.querySelector('.bb-gwp__selection');
        if (label) label.textContent = config.forced ? (selected ? 'Included' : 'Automatic sample') : (selected ? 'Remove' : 'Select');
      });
    }

    message(text) {
      const element = document.getElementById('BBLegacyGWPMessage');
      clearTimeout(this.messageTimer);
      element.textContent = text;
      element.hidden = false;
      this.messageTimer = setTimeout(() => { element.hidden = true; }, 10000);
    }

    onClick(event) {
      const wrapButton = event.target.closest('[data-gift-wrapping]');
      if (wrapButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.busy && !wrapButton.disabled) {
          const inDrawer = !!wrapButton.closest('cart-drawer');
          this.run(null, true).then(() => {
            document.querySelector(inDrawer ? 'cart-drawer' : 'cart-items')?.querySelector('[data-gift-wrapping]')?.focus({ preventScroll: true });
          });
        }
        return;
      }
      const button = event.target.closest('[data-gwp-product]');
      if (button) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!this.busy && !config.forced && !button.disabled) {
          const productId = Number(button.dataset.gwpProduct);
          const inDrawer = !!button.closest('cart-drawer');
          this.run(productId, false, button.hasAttribute('data-gwp-select-only')).then(() => {
            const scope = document.querySelector(inDrawer ? 'cart-drawer' : 'cart-items');
            scope?.querySelector(`[data-gwp-product="${productId}"]`)?.focus({ preventScroll: true });
          });
        }
      } else if ((this.busy || this.needsRefresh) && event.target.closest('cart-remove-button, quantity-input, [name="checkout"], .cart__dynamic-checkout, a[href*="/checkout"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.message(this.needsRefresh ? 'Please refresh the page before checkout.' : 'Please wait for your cart to finish updating.');
      }
    }
  }

  window.BBLegacyGWP = new GWPController();
})();
